import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "antd/dist/reset.css";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { ErrorBoundary } from "./app/components/ErrorBoundary";
import NavBar from "./app/components/NavBar";
import Index from "./app/pages/Home";
import Login from "./app/pages/Login";
import NotFound from "./app/pages/NotFound";
import Register from "./app/pages/Register";
import UserPolicies from "./app/pages/UserPolicies";
import UserProfile from "./app/pages/UserProfile";
import ClaimDetails from "./app/pages/claims/ClaimDetails";
import Dashboard from "./app/pages/dashboard/Dashboard";
import Home from "./app/pages/dashboard/Home";
import PolicyDetails from "./app/pages/policies/PolicyDetails";
import Contribute from "./app/pages/public/Contribute";
import FAQs from "./app/pages/public/FrequentlyAskedQuestions";
import "./index.css";
import { store } from "./redux/store";
import reportWebVitals from "./reportWebVitals";

import { isLoggedIn } from "axios-jwt";
import PublicProfile from "./app/components/users/PublicProfile";
import ForgotPassword from "./app/pages/ForgotPassword";
import ClaimCreate from "./app/pages/claims/ClaimCreate";
import PolicyPoolDetails from "./app/pages/dashboard/PolicyPoolDetails";
import PolicySettings from "./app/pages/policies/PolicySettings";
import ContributeMobileDetails from "./app/pages/public/ContributeMobileDetails";
import Referral from "./app/pages/public/Referral";
import Refund from "./app/pages/public/Refund";

const RouteWithErrorBoundary = ({ element }: { element: React.ReactElement }) => (
    <ErrorBoundary>{element}</ErrorBoundary>
);

const App = () => {
    let loggedIn = isLoggedIn();

    return (
        <BrowserRouter>
            <ErrorBoundary>
                {!loggedIn && <NavBar />}
                <Routes>
                    <Route path="/join" element={<RouteWithErrorBoundary element={<Register />} />} />
                    <Route path="/login" element={<RouteWithErrorBoundary element={<Login />} />} />
                    <Route path="/forgot-password" element={<RouteWithErrorBoundary element={<ForgotPassword />} />} />
                    <Route path="/faqs" element={<RouteWithErrorBoundary element={<FAQs />} />} />
                    <Route path="/refund" element={<RouteWithErrorBoundary element={<Refund />} />} />
                    <Route path="/referral" element={<RouteWithErrorBoundary element={<Referral />} />} />
                    <Route path="/contribute" element={<RouteWithErrorBoundary element={<Contribute />} />} />
                    <Route
                        path="/contribute/stats"
                        element={<RouteWithErrorBoundary element={<ContributeMobileDetails />} />}
                    />
                    {!loggedIn && <Route path="/" element={<RouteWithErrorBoundary element={<Index />} />} />}
                    <Route path="/" element={<RouteWithErrorBoundary element={<Dashboard />} />}>
                        <Route path="home" element={<RouteWithErrorBoundary element={<Home />} />} />
                        <Route
                            path="policy/:id"
                            element={<RouteWithErrorBoundary element={<PolicyDetails />} />}
                        />
                        <Route
                            path="policy/:id/settings"
                            element={<RouteWithErrorBoundary element={<PolicySettings />} />}
                        />
                        <Route
                            path="policy/:id/claims/new"
                            element={<RouteWithErrorBoundary element={<ClaimCreate />} />}
                        />
                        <Route
                            path="policy/:id/claims/:claimId"
                            element={<RouteWithErrorBoundary element={<ClaimDetails />} />}
                        />
                        <Route
                            path="policy/:id/pool"
                            element={<RouteWithErrorBoundary element={<PolicyPoolDetails />} />}
                        />
                        <Route
                            path="policies"
                            element={<RouteWithErrorBoundary element={<UserPolicies />} />}
                        />
                        <Route path="me" element={<RouteWithErrorBoundary element={<UserProfile />} />} />
                        <Route
                            path="/members/:id"
                            element={<RouteWithErrorBoundary element={<PublicProfile />} />}
                        />
                        <Route path="*" element={<RouteWithErrorBoundary element={<NotFound />} />} />
                    </Route>
                </Routes>
            </ErrorBoundary>
        </BrowserRouter>
    );
};

const container = document.getElementById("root")!;
const root = createRoot(container);
let persistor = persistStore(store);

// Create a React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

console.log(
    "Note that strict mode is on which will cause double renders during development. Builds not affected."
);

root.render(
    <Provider store={store}>
        <PersistGate persistor={persistor}>
            <QueryClientProvider client={queryClient}>
                <App />
            </QueryClientProvider>
        </PersistGate>
    </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
